// Shared helpers for the Casino Dashboard pages ported from the
// casino_dashboard project. The number handling mirrors the originals exactly
// (the API returns pre-formatted strings like "12,345.67" in places), so the
// figures on screen stay identical.
import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { tokens } from "../../theme";

export const mono = '"IBM Plex Mono", monospace';
export const disp = '"Space Grotesk", sans-serif';

// Strip thousands separators the API sends inside strings.
const strip = (v) => (typeof v === "string" ? v.replace(/,/g, "") : v);

// Sum of two API amounts, as a fixed-2 string. Mirrors getTotal() in the
// original data files: a null on either side collapses the result to 0.
export const getTotal = (amt1, amt2 = "0.0") => {
  if (amt1 == null) return "0.00";
  if (amt2 == null) return "0.00";
  const a = parseFloat(strip(amt1));
  const b = parseFloat(strip(amt2));
  if (Number.isNaN(a) || Number.isNaN(b)) return "0.00";
  return (a + b).toFixed(2);
};

// Difference of two API amounts, as a fixed-2 string.
export const getDifferent = (amt1, amt2) => {
  const a = parseFloat(strip(amt1 ?? 0));
  const b = parseFloat(strip(amt2 ?? 0));
  if (Number.isNaN(a) || Number.isNaN(b)) return "0.00";
  return (a - b).toFixed(2);
};

// 1234.5 → "1,234.50"
export const formatMoney = (amount) => {
  if (amount == null) return "0.00";
  const n = parseFloat(strip(amount));
  if (Number.isNaN(n)) return "0.00";
  return n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

export const money = (amount) => `$${formatMoney(amount)}`;

// Safe .toFixed for raw numeric fields coming off the API.
export const fixed = (v, digits = 2) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : (0).toFixed(digits);
};

// Green when the house is up, red when it is down — the original used the
// theme's success/primary colours for the same signal.
export function ProfitChip({ value, suffix = "", positive }) {
  const isUp = positive != null ? positive : parseFloat(String(value).replace(/,/g, "")) > 0;
  return (
    <Box
      component="span"
      sx={{
        fontFamily: mono,
        fontSize: 11.5,
        fontWeight: 700,
        px: 0.9,
        py: "3px",
        borderRadius: "6px",
        display: "inline-block",
        bgcolor: isUp ? "rgba(35,214,160,0.15)" : "rgba(242,92,102,0.15)",
        color: isUp ? tokens.secondary : tokens.danger,
      }}
    >
      {value}
      {suffix}
    </Box>
  );
}

// Today / Yesterday / Last Week / … selector used by several pages.
export function RangeButtons({ labels, active, onChange, disabled }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {labels.map((label, index) => {
        const isActive = active === index;
        return (
          <Button
            key={label}
            size="small"
            disabled={disabled}
            onClick={() => onChange(index)}
            variant={isActive ? "contained" : "outlined"}
            color={isActive ? "primary" : "inherit"}
            sx={{
              fontSize: 12.5,
              py: 0.4,
              ...(isActive
                ? {}
                : { color: tokens.textDim, borderColor: tokens.border }),
            }}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );
}

// Consistent inline status line for the ported pages.
export function StateLine({ loading, error, empty, emptyText = "No data available." }) {
  if (loading) {
    return (
      <Typography sx={{ py: 4, textAlign: "center", color: tokens.textDim, fontSize: 13 }}>
        Loading…
      </Typography>
    );
  }
  if (error) {
    return (
      <Typography sx={{ py: 4, textAlign: "center", color: tokens.danger, fontSize: 13 }}>
        {error}
      </Typography>
    );
  }
  if (empty) {
    return (
      <Typography sx={{ py: 4, textAlign: "center", color: tokens.textDim, fontSize: 13 }}>
        {emptyText}
      </Typography>
    );
  }
  return null;
}

// Human message for a failed request.
export const errText = (e) =>
  e?.response?.data?.message || e?.message || "Request failed. Please try again.";

// Period rows shared by the Overview summary tables.
export const PERIODS = [
  { label: "Today", key: "today" },
  { label: "Yesterday", key: "Yesterday" },
  { label: "Last Week", key: "lastWeek" },
  { label: "Last Month", key: "lastMonth" },
  { label: "Total", key: "totalSum" },
];
